/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

// Services
import { GetMeetingsArgs, MeetingService } from "@/services/meeting.service";

/* -------------------------------------------------------------------------- */
/*                              Get Meetings Hook                             */
/* -------------------------------------------------------------------------- */
const meetingService = new MeetingService();

type UseMeetingsOptions = {
  params?: GetMeetingsArgs;
};

const useMeetings = ({ params }: UseMeetingsOptions = {}) => {
  const { data, isPending, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      queryFn: async ({ pageParam }) => {
        return await meetingService.get({
          meetingDate: params?.meetingDate,
          search: params?.search,
          status: params?.status,
          startDate: params?.startDate,
          endDate: params?.endDate,
          page: pageParam,
          pageSize: params?.pageSize,
          reportDone: params?.reportDone,
        });
      },
      queryKey: ["meetings", params],
      placeholderData: keepPreviousData,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const nextPage = url.searchParams.get("page");
          return nextPage ? Number(nextPage) : undefined;
        }
        return undefined;
      },
    });

  return {
    data,
    isPending,
    fetchNextPage,
    hasNextPage: hasNextPage && data?.pages[data.pages.length - 1].next,
    isFetching,
  };
};

export { useMeetings };
